import React, { useEffect, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, ListRow, Spinner, Text } from '@betterment/samba-react';

import {
  BillingPlanAssociatedHouseholdsDocument,
  BillingPlanAssociatedHouseholdsQuery,
} from './query.billing_plan_associated_households.generated';

type BillingPlanAssociatedHouseholdsType = BillingPlanAssociatedHouseholdsQuery['billingPlanAffectedHouseholds'];

const HOUSEHOLD_LIMIT = 10;

const LoadMoreCTA: React.FC<{
  hasMoreHouseholds: boolean;
  handleLoadMore: () => void;
}> = ({ hasMoreHouseholds, handleLoadMore }) => {
  if (!hasMoreHouseholds) return null;
  return (
    <Box css={{ p: 'space.300' }}>
      <Button variant="textPrimary" onClick={handleLoadMore}>
        Load more
      </Button>
    </Box>
  );
};

const BaseRow: React.FC<{
  loadedHouseholds: BillingPlanAssociatedHouseholdsType;
}> = ({ loadedHouseholds }) => {
  return loadedHouseholds.map(household => (
    <ListRow
      css={{ px: 0, mx: 'space.300', borderBottom: 'token(borderWidths.thin) solid token(colors.border.weak)' }}
      key={household.id}
    >
      <ListRow.LeadingContent>
        <Box css={{ flex: 8 }}>
          <Text variant="text100">{household.name}</Text>
        </Box>
      </ListRow.LeadingContent>
    </ListRow>
  ));
};

export const HouseholdRow: React.FC<{
  planId: string;
  searchText: string;
  numberOfClients: number;
  setShouldShowAssignedClients: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ planId, numberOfClients, searchText, setShouldShowAssignedClients }) => {
  const [loadedHouseholds, setLoadedHouseholds] = useState<BillingPlanAssociatedHouseholdsType>([]);
  const [currentOffset, setCurrentOffset] = useState(0);

  const { data, loading: isLoading } = useGraphQLQuery(BillingPlanAssociatedHouseholdsDocument, {
    variables: {
      billingPlanId: planId,
      offset: currentOffset,
      searchText,
    },
  });

  useEffect(() => {
    if (data?.billingPlanAffectedHouseholds) {
      if (currentOffset === 0) {
        setLoadedHouseholds(data.billingPlanAffectedHouseholds);
      } else {
        setLoadedHouseholds(prev => [...prev, ...data.billingPlanAffectedHouseholds]);
      }
    }
  }, [data, currentOffset]);

  useEffect(() => {
    setShouldShowAssignedClients(!isLoading);
  }, [data, isLoading, setShouldShowAssignedClients]);

  const hasMoreHouseholds = currentOffset + HOUSEHOLD_LIMIT < numberOfClients;

  const handleLoadMore = () => {
    setCurrentOffset(prev => prev + HOUSEHOLD_LIMIT);
  };

  return isLoading ? (
    <Spinner css={{ alignSelf: 'center', justifySelf: 'center', my: 'space.800' }} variant="secondary" size="xs" />
  ) : (
    <>
      <BaseRow loadedHouseholds={loadedHouseholds} />
      <LoadMoreCTA hasMoreHouseholds={hasMoreHouseholds} handleLoadMore={handleLoadMore} />
    </>
  );
};
